import { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import {
  KEY_ARROW_DOWN,
  KEY_ARROW_UP,
  KEY_ENTER,
  KEY_TAB,
} from "constants/keyboardKeys";

export type SuggestionPopup<TItem> = {
  items: TItem[];
  query: string;
  highlightedIndex: number;
  onSelect: (item: TItem) => void;
  onHighlight: (item: TItem) => void;
};

type SuggestionPopupRendererArgs<TItem, TSelected> = {
  onSelect: (item: TItem, command: (props: TSelected) => void) => void;
  onPopupChange: (popup: SuggestionPopup<TItem> | null) => void;
};

export type SuggestionPopupRenderer<TItem, TSelected> = {
  render: NonNullable<SuggestionOptions<TItem, TSelected>["render"]>;
  isOpen: () => boolean;
};

/**
 * The suggestion plugin drives the popup imperatively, so this keeps the
 * active props + highlighted row in closure and publishes a snapshot to React.
 */
export const createSuggestionPopupRenderer = <TItem, TSelected>({
  onSelect,
  onPopupChange,
}: SuggestionPopupRendererArgs<TItem, TSelected>): SuggestionPopupRenderer<
  TItem,
  TSelected
> => {
  let activeProps: SuggestionProps<TItem, TSelected> | null = null;
  let highlightedIndex = 0;

  const getItems = () => activeProps?.items ?? [];

  const select = (item: TItem) => {
    if (activeProps) onSelect(item, activeProps.command);
  };

  const highlight = (item: TItem) => {
    const index = getItems().indexOf(item);
    if (index === -1 || index === highlightedIndex) return;

    highlightedIndex = index;
    publish();
  };

  const publish = () => {
    if (!activeProps || getItems().length === 0) {
      onPopupChange(null);
      return;
    }

    onPopupChange({
      items: activeProps.items,
      query: activeProps.query,
      highlightedIndex,
      onSelect: select,
      onHighlight: highlight,
    });
  };

  const open = (props: SuggestionProps<TItem, TSelected>) => {
    activeProps = props;
    highlightedIndex = 0;
    publish();
  };

  const moveHighlight = (step: number) => {
    const count = getItems().length;
    highlightedIndex = (highlightedIndex + step + count) % count;
    publish();
    return true;
  };

  return {
    isOpen: () => getItems().length > 0,
    render: () => ({
      onStart: open,
      onUpdate: open,
      onExit: () => {
        activeProps = null;
        publish();
      },
      onKeyDown: ({ event }) => {
        const items = getItems();
        if (items.length === 0) return false;

        if (event.key === KEY_ARROW_DOWN) return moveHighlight(1);
        if (event.key === KEY_ARROW_UP) return moveHighlight(-1);
        if (event.key === KEY_ENTER || event.key === KEY_TAB) {
          select(items[highlightedIndex]);
          return true;
        }

        return false;
      },
    }),
  };
};
